/**
 * Room Availability Dashboard
 * Shows booking status and room availability with date range search
 * Available for Front Office and Hotel Admin
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Calendar, Search, RefreshCw, Hotel, Users, DoorOpen, 
  CheckCircle2, XCircle, Clock, AlertTriangle, Filter
} from 'lucide-react';

// Status colors
const statusColors = {
  available: 'bg-green-100 text-green-700 border-green-300',
  occupied: 'bg-red-100 text-red-700 border-red-300',
  reserved: 'bg-blue-100 text-blue-700 border-blue-300',
  maintenance: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  blocked: 'bg-gray-100 text-gray-700 border-gray-300',
  checkout_pending: 'bg-orange-100 text-orange-700 border-orange-300',
};

// Room Card for visual representation
const RoomCard = ({ room, onClick }) => {
  const status = room.booking_status || room.status || 'available';
  const colorClass = statusColors[status] || statusColors.available;
  
  return (
    <div 
      className={`p-3 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${colorClass}`}
      onClick={() => onClick(room)}
      data-testid={`room-card-${room.room_number}`}
    >
      <div className="text-center">
        <p className="font-bold text-lg">{room.room_number}</p>
        <p className="text-xs opacity-75">{room.room_type_name || room.room_type}</p>
        {room.guest_name && (
          <p className="text-xs mt-1 truncate">{room.guest_name}</p>
        )}
        {room.checkout_date && status === 'occupied' && (
          <p className="text-xs mt-1">Out: {new Date(room.checkout_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</p>
        )}
      </div>
    </div>
  );
};

export default function RoomAvailability({ user }) {
  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [activeTab, setActiveTab] = useState('visual');
  
  // Filters
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [roomTypeFilter, setRoomTypeFilter] = useState('all');
  const [floorFilter, setFloorFilter] = useState('all');
  
  // Selected room details
  const [selectedRoom, setSelectedRoom] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [roomsRes, bookingsRes, typesRes] = await Promise.all([
        api.get('/rooms'),
        api.get(`/bookings?start_date=${startDate}&end_date=${endDate}`).catch(() => ({ data: [] })),
        api.get('/room-types').catch(() => ({ data: [] }))
      ]);
      
      // Enrich rooms with booking info
      const roomsData = roomsRes.data || [];
      const bookingsData = bookingsRes.data || [];
      
      const enrichedRooms = roomsData.map(room => {
        // Find active booking for this room
        const activeBooking = bookingsData.find(b => 
          b.room_id === room.id && 
          ['confirmed', 'checked_in'].includes(b.status)
        );
        
        if (activeBooking) {
          return {
            ...room,
            booking_status: activeBooking.status === 'checked_in' ? 'occupied' : 'reserved',
            guest_name: activeBooking.guest_name || `${activeBooking.primary_guest?.first_name || ''} ${activeBooking.primary_guest?.last_name || ''}`.trim(),
            booking_id: activeBooking.id,
            checkin_date: activeBooking.check_in_date,
            checkout_date: activeBooking.check_out_date,
          };
        }
        
        return {
          ...room,
          booking_status: room.status || 'available',
        };
      });
      
      setRooms(enrichedRooms);
      setBookings(bookingsData);
      setRoomTypes(typesRes.data || []);
    } catch (error) {
      toast.error('Failed to load room data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get unique floors
  const floors = [...new Set(rooms.map(r => r.floor || '1'))].sort((a, b) => Number(a) - Number(b));

  // Filter rooms
  const filteredRooms = rooms.filter(room => {
    if (statusFilter !== 'all' && room.booking_status !== statusFilter) return false;
    if (roomTypeFilter !== 'all' && room.room_type_id !== roomTypeFilter) return false;
    if (floorFilter !== 'all' && String(room.floor) !== floorFilter) return false;
    return true;
  });

  // Group rooms by floor
  const roomsByFloor = floors.reduce((acc, floor) => {
    acc[floor] = filteredRooms.filter(r => String(r.floor || '1') === String(floor));
    return acc;
  }, {});

  // Calculate stats
  const stats = {
    total: rooms.length,
    available: rooms.filter(r => r.booking_status === 'available').length,
    occupied: rooms.filter(r => r.booking_status === 'occupied').length,
    reserved: rooms.filter(r => r.booking_status === 'reserved').length,
    maintenance: rooms.filter(r => r.booking_status === 'maintenance' || r.booking_status === 'blocked').length,
  };

  const handleRoomClick = (room) => {
    setSelectedRoom(room);
  };

  return (
    <div className="p-6 space-y-6" data-testid="room-availability-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Room Availability</h1>
          <p className="text-gray-500">View room status and booking availability</p>
        </div>
        <Button onClick={loadData} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Hotel className="h-6 w-6 mx-auto text-gray-500 mb-1" />
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-gray-500">Total Rooms</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4 text-center">
            <CheckCircle2 className="h-6 w-6 mx-auto text-green-500 mb-1" />
            <p className="text-2xl font-bold text-green-700">{stats.available}</p>
            <p className="text-xs text-green-600">Available</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-center">
            <Users className="h-6 w-6 mx-auto text-red-500 mb-1" />
            <p className="text-2xl font-bold text-red-700">{stats.occupied}</p>
            <p className="text-xs text-red-600">Occupied</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4 text-center">
            <Clock className="h-6 w-6 mx-auto text-blue-500 mb-1" />
            <p className="text-2xl font-bold text-blue-700">{stats.reserved}</p>
            <p className="text-xs text-blue-600">Reserved</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="h-6 w-6 mx-auto text-yellow-500 mb-1" />
            <p className="text-2xl font-bold text-yellow-700">{stats.maintenance}</p>
            <p className="text-xs text-yellow-600">Maintenance</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">From Date</Label>
              <Input 
                type="date" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To Date</Label>
              <Input 
                type="date" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="occupied">Occupied</SelectItem>
                  <SelectItem value="reserved">Reserved</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Room Type</Label>
              <Select value={roomTypeFilter} onValueChange={setRoomTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {roomTypes.map(rt => (
                    <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Floor</Label>
              <Select value={floorFilter} onValueChange={setFloorFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Floors</SelectItem>
                  {floors.map(floor => (
                    <SelectItem key={floor} value={String(floor)}>Floor {floor}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={loadData} className="w-full">
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-200 border border-green-400"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-200 border border-red-400"></div>
          <span>Occupied</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-200 border border-blue-400"></div>
          <span>Reserved</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-yellow-200 border border-yellow-400"></div>
          <span>Maintenance</span>
        </div>
      </div>

      {/* Room Display */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="visual">
            <DoorOpen className="h-4 w-4 mr-2" />
            Visual View
          </TabsTrigger>
          <TabsTrigger value="list">
            <Filter className="h-4 w-4 mr-2" />
            List View
          </TabsTrigger>
        </TabsList>

        {/* Visual View */}
        <TabsContent value="visual" className="mt-4">
          {loading ? (
            <div className="text-center py-12 text-gray-500">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
              <p>Loading rooms...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(roomsByFloor).map(([floor, floorRooms]) => (
                <Card key={floor}>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm font-medium">Floor {floor}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3">
                      {floorRooms.map(room => (
                        <RoomCard 
                          key={room.id} 
                          room={room} 
                          onClick={handleRoomClick}
                        />
                      ))}
                    </div>
                    {floorRooms.length === 0 && (
                      <p className="text-center text-gray-400 py-4">No rooms on this floor match the filters</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* List View */}
        <TabsContent value="list" className="mt-4">
          <Card>
            <CardContent className="p-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Room</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-left p-2">Floor</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Guest</th>
                      <th className="text-left p-2">Check-out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRooms.map(room => (
                      <tr 
                        key={room.id} 
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleRoomClick(room)}
                      >
                        <td className="p-2 font-medium">{room.room_number}</td>
                        <td className="p-2">{room.room_type_name || room.room_type || '-'}</td>
                        <td className="p-2">{room.floor || '1'}</td>
                        <td className="p-2">
                          <Badge variant="outline" className={statusColors[room.booking_status]}>
                            {room.booking_status || 'available'}
                          </Badge>
                        </td>
                        <td className="p-2">{room.guest_name || '-'}</td>
                        <td className="p-2">
                          {room.checkout_date 
                            ? new Date(room.checkout_date).toLocaleDateString('en-IN') 
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Selected Room Details Panel */}
      {selectedRoom && (
        <Card className="fixed bottom-4 right-4 w-80 shadow-lg z-50">
          <CardHeader className="py-3 flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Room {selectedRoom.room_number}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setSelectedRoom(null)}>×</Button>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Type</span>
                <span>{selectedRoom.room_type_name || selectedRoom.room_type || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Floor</span>
                <span>{selectedRoom.floor || '1'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <Badge variant="outline" className={statusColors[selectedRoom.booking_status]}>
                  {selectedRoom.booking_status}
                </Badge>
              </div>
              {selectedRoom.guest_name && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Guest</span>
                  <span>{selectedRoom.guest_name}</span>
                </div>
              )}
              {selectedRoom.checkin_date && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Check-in</span>
                  <span>{new Date(selectedRoom.checkin_date).toLocaleDateString('en-IN')}</span>
                </div>
              )}
              {selectedRoom.checkout_date && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Check-out</span>
                  <span>{new Date(selectedRoom.checkout_date).toLocaleDateString('en-IN')}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
